import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import dropDownStyle from "components/dropdown-menu/dropdown-menu.scss";
import { DropdownMenu } from "components/dropdown-menu/DropdownMenu";
import ToggleablePanel from "components/header/ToggleablePanel";
import Chevron from "components/icons/Chevron";
import ExportIcon from "components/icons/ExportIcon";
import KebabIcon from "components/icons/KebabIcon";
import { createLicenseTypes, fetchDynamicTierLicenses, isCommonLicense } from "components/licenses/common";
import style from "components/products/getting-started.scss";
import product from "components/products/product-card/product-card.scss";
import Tooltip from "components/tooltip/Tooltip";
import userMenuStyle from "components/user-menu/user-menu.scss";
import { LicenseData } from "domain/licenses";
import { OnboardingImage, SoftwarePackage, Versions } from "domain/softwarePackages";
import { getUserTier, isLicensingModelBundleWithToken } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import { createShaChecksumMenuItem } from "utils/shaChecksumUtils";
import { useOutsideClick } from "utils/useOutsideClick";

import testIds from "testIds.json";

interface Props {
    titleKey: string;
    descriptionKey: string;
    onDownload: () => void;
    handleDropdown?: boolean;
    handleIsoDropdown?: boolean;
    fetchedSoftwarePackages?: SoftwarePackage[];
    fetchedLicenses?: LicenseData[];
    featureWorkflowLicensePresent?: boolean;
    fetchedOnboardingImages: OnboardingImage[];
    forConfigurationTool?: boolean;
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
    user: state.userReducer.user,
});

const connector = connect(mapState);

const ProductCard = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const buttonRef = React.useRef(null);

    function handleClickOutside() {
        setExpanded(false);
    }
    const versionUrlRef = useOutsideClick(handleClickOutside);

    const [versionToUrl, setVersionToUrl] = React.useState<Map<string, string>>(new Map());
    const [filteredPackages, setFilteredPackages] = React.useState<SoftwarePackage>();
    const [filteredImages, setFilteredImages] = React.useState<OnboardingImage[]>();

    const [isoFiles, setIsoFiles] = React.useState<Map<string, string>>(new Map());
    const bdeIds = ["20", "36", "50"];
    function extractCorrectProduct() {
        const productType = new Map();

        if (!props.fetchedLicenses || props.fetchedLicenses.length === 0) {
            return;
        }

        const hasCommonLicense = props.fetchedLicenses.some(
            (each) => isCommonLicense(each.type) && isLicensingModelBundleWithToken()
        );

        if (hasCommonLicense) {
            const licenseTypes = createLicenseTypes(false);
            const userTier = getUserTier();
            const tierLicenses = fetchDynamicTierLicenses(userTier);

            if (tierLicenses) {
                bdeIds.forEach((type) => {
                    if (tierLicenses.includes(type)) {
                        const licenseType = licenseTypes.find((each) => each.productId === type);
                        if (licenseType) {
                            productType.set(type, licenseType.productName);
                        }
                    }
                });
            }
        } else {
            props.fetchedLicenses.forEach((each) => {
                if (bdeIds.includes(each.type)) {
                    productType.set(each.type, each.license);
                }
            });
        }

        fetchDownloadUrl(props.featureWorkflowLicensePresent as boolean, productType);
    }

    function fetchDownloadUrl(featureWorkflowLicensePresent: boolean, productType: Map<string, string>) {
        const foundFiles = new Map();
        productType.forEach((keys, values) => {
            const productConfig = props.fetchedOnboardingImages?.find(
                (product) => product.licenseId === parseInt(values)
            );
            const fileUrl = featureWorkflowLicensePresent ? productConfig?.workflow : productConfig?.manual;
            if (fileUrl) {
                foundFiles.set(keys, fileUrl);
            }
        });
        setIsoFiles(foundFiles);
    }

    function isForConfigurationTool(productName: string) {
        return productName.toLowerCase().includes("configuration");
    }

    React.useEffect(() => {
        const versionToUrlMapping = new Map();
        props.fetchedSoftwarePackages?.map((product) => {
            if (isForConfigurationTool(product.productName)) {
                return product.versions.map((toolVersion) => {
                    versionToUrlMapping.set(toolVersion.version.split(" ").pop(), toolVersion.downloadUrl);
                });
            }
            return null;
        });
        setVersionToUrl(versionToUrlMapping);
        extractCorrectProduct();
        if (props.forConfigurationTool) {
            setFilteredPackages(
                props.fetchedSoftwarePackages?.find((product) => isForConfigurationTool(product.productName))
            );
        } else {
            setFilteredPackages(
                props.fetchedSoftwarePackages?.find((product) => !isForConfigurationTool(product.productName))
            );
        }

        setFilteredImages(props.fetchedOnboardingImages?.filter((item) => bdeIds.includes(item.licenseId.toString())));
    }, [
        props.fetchedOnboardingImages,
        props.fetchedSoftwarePackages,
        props.fetchedLicenses,
        props.featureWorkflowLicensePresent,
        props.forConfigurationTool,
    ]);

    function onWindowsClick() {
        window.location.href = versionToUrl.get("Windows") as string;
    }
    function onLinuxClick() {
        window.location.href = versionToUrl.get("Linux") as string;
    }
    function isoClick(value: string) {
        window.location.href = value;
    }

    function toastForShaKeys(productName: string, index: number, sha: string | Versions[] | undefined) {
        if (!sha) {
            return null;
        }
        const formattedSha = typeof sha === "string" ? sha : sha[index]?.sha256;
        const formattedItemTitle =
            props.forConfigurationTool && typeof sha !== "string" ? sha[index]?.version : productName;

        if (!formattedSha || !formattedItemTitle) {
            return null;
        }

        return createShaChecksumMenuItem(
            formattedSha,
            productName,
            formattedItemTitle,
            index,
            t,
            props.theme.iconFillColor,
            props.theme.whiteColor
        );
    }

    function VerifyDownloadFileDropdown() {
        return (
            <ToggleablePanel
                title={""}
                testId={testIds.workArea.onboarding.step.kebabMenu.button}
                menuType={"HEADER"}
                icon={
                    <Tooltip content={t("Onboarding.home.downloadIconTooltip")}>
                        <KebabIcon color={props.theme.iconFillColor} />
                    </Tooltip>
                }
                hideChevron={true}
            >
                <DropdownMenu
                    position={"RIGHT"}
                    testId={testIds.workArea.onboarding.step.kebabMenu.itself}
                    menuItems={[
                        <ToggleablePanel
                            key={"downloadFile"}
                            title={t("Onboarding.home.verifyDownloadFile")}
                            testId={testIds.workArea.onboarding.step.kebabMenu.versionListMenu.button}
                            menuType={"HEADER"}
                            buttonClass={product.menuButtonWithChevron}
                            chevronSize={{ width: 14, height: 14 }}
                            customToggleIcon={
                                <Chevron
                                    color={props.theme.textColor}
                                    className={userMenuStyle.chevronRight}
                                    width={16}
                                    height={16}
                                />
                            }
                            hideChevron
                        >
                            <DropdownMenu
                                position={"LEFT"}
                                testId={testIds.workArea.onboarding.step.kebabMenu.versionListMenu.itself}
                                className={classNames(dropDownStyle.dropdownMenu && dropDownStyle.nestedDropDown)}
                                menuItems={
                                    props.forConfigurationTool
                                        ? filteredPackages?.versions.map((_, versionIndex) => (
                                              <>
                                                  {toastForShaKeys(
                                                      filteredPackages.productName,
                                                      versionIndex,
                                                      filteredPackages?.versions
                                                  )}
                                              </>
                                          ))
                                        : filteredImages?.map((image, index) => (
                                              <>
                                                  {toastForShaKeys(
                                                      image.manualProductName,
                                                      index,
                                                      props.featureWorkflowLicensePresent
                                                          ? image.shaForWorkflow
                                                          : image.shaForManual
                                                  )}
                                              </>
                                          ))
                                }
                            />
                        </ToggleablePanel>,
                    ]}
                />
            </ToggleablePanel>
        );
    }

    return (
        <>
            <div className={style.productCard}>
                <div className={product.productCardWrapper} ref={versionUrlRef}>
                    <strong>{t(props.titleKey)}</strong>
                    <div className={style.buttonWrapper}>
                        {isoFiles.size === 1 ? (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = isoFiles.values().next().value;
                                }}
                            >
                                <ExportIcon color={props.theme.iconFillColor} />
                            </button>
                        ) : (
                            <button
                                ref={buttonRef}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.onDownload();
                                    setExpanded((prev) => !prev);
                                }}
                                aria-expanded={expanded}
                            >
                                <ExportIcon color={props.theme.iconFillColor} />
                            </button>
                        )}
                        <VerifyDownloadFileDropdown />
                    </div>

                    {expanded && props.handleDropdown && (
                        <div className={style.version}>
                            <button className={style.versionLink} onClick={onWindowsClick}>
                                {t("Onboarding.gettingStartedFlow.preConfigureBlanccoDriveErasure.windows")}
                            </button>

                            <button className={style.versionLink} onClick={onLinuxClick}>
                                {t("Onboarding.gettingStartedFlow.preConfigureBlanccoDriveErasure.linux")}
                            </button>
                        </div>
                    )}

                    {expanded && props.handleIsoDropdown && (
                        <div className={style.isoFile}>
                            {Array.from(isoFiles).map(([key, value]) => {
                                if (isoFiles.size !== 1) {
                                    return (
                                        <button
                                            key={key}
                                            className={style.versionLink}
                                            onClick={() => isoClick(value as string)}
                                        >
                                            {key}
                                        </button>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    )}
                </div>
                <div className={product.description}>{t(props.descriptionKey)}</div>
            </div>
        </>
    );
};

export default connector(ProductCard);
